import CertificateIcon from "@/static/media/icons/certificate-icon.svg?react"
import SparkleLine from "@/static/media/icons/sparkle-line.svg?react"
import ToolsIcons from "@/static/media/icons/tools-icons.svg?react"
import { useState } from "react"
import { useAppDispatch } from "@/app/hooks/reduxHooks"
import { closeCompanyDetails } from "@/features/searchTable/slice/companyDetailsSlice"
import { Avatar } from "@radix-ui/react-avatar"
import { Eye, Globe, GraduationCap, Mail, Phone, User, X } from "lucide-react"
import AdobeIcon from "../../static/media/icons/adobe-icon.svg?react"
import ArmchairIcon from "../../static/media/icons/armchair-icon.svg?react"
import BriefcaseIcon from "../../static/media/icons/briefcase-icon.svg?react"
import FbIcon from "../../static/media/icons/fb-icon.svg?react"
import LinkedinIcon from "../../static/media/icons/linkedin_icon.svg?react"
import { Badge } from "./badge"
import { Button } from "./button"
import { Card, CardContent, CardHeader, CardTitle } from "./card"
const CompanyDetails = () => {
  const dispatch = useAppDispatch()
  const [revealedFields, setRevealedFields] = useState<Set<string>>(new Set())

  const toggleReveal = (field: string) => {
    const updated = new Set(revealedFields)
    if (updated.has(field)) {
      updated.delete(field)
    } else {
      updated.add(field)
    }
    setRevealedFields(updated)
  }

  const revealAllInfo = () => {
    setRevealedFields(new Set(["phone", "secondaryPhone", "officialEmail", "personalEmail"]))
  }

  const maskEmail = (email: string) => {
    if (!email) return ""
    const [_, domain] = email.split("@")
    const maskedUsername = "• • • •"
    return `${maskedUsername} @${domain}`
  }

  const maskPhone = (phone: string) => {
    if (!phone) return ""
    const lastFour = phone.slice(-4)
    return `• • • • ${lastFour}`
  }
  const educationData = [
    {
      degree: "MBA, Business Administration",
      school: "Stanford University",
      location: "California, US",
      time: "2016 - 2018"
    },
    {
      degree: "BS, Computer Science",
      school: "UC Berkeley",
      location: "California, US",
      time: "2012 - 2016"
    }
  ]

  const certificationsData = [
    {
      name: "Certified Scrum Product Owner (CSPO)",
      category: "Scrum Alliance",
      date: "2024-01-15"
    },
    {
      name: "Google Analytics Certified...",
      category: "Google",
      date: "2024-01-08"
    },
    {
      name: "Product Management Certification...",
      category: "Product School",
      date: "2024-01-01"
    }
  ]

  return (
    <div className=" min-h-screen items-center justify-center overflow-y-auto pb-5">
      {/* Header */}
      <div className="relative mx-auto flex w-full max-w-[65rem] items-center  py-5">
        <Avatar className="flex size-8 items-center justify-center rounded-full border">
          <User className="size-5" />
        </Avatar>
        <div className="ml-2 flex flex-col">
          <span className="text-[18px] font-medium text-gray-950">Sarah Johnson</span>
          <span className="text-sm font-normal text-gray-600">Senior Product Manager • Chicago, IL</span>
        </div>

        <Button variant="ghost" size="icon" className="absolute right-0" onClick={() => dispatch(closeCompanyDetails())}>
          <X className="size-4" />
        </Button>
      </div>
      <div className="border-b"></div>

      {/* main content */}

      <div className="mt-4 flex items-start justify-center gap-4">
        <div className="flex flex-col items-center justify-center">
          <Card className="max-w-2xl p-4">
            <CardHeader className="p-0">
              <CardTitle className="flex items-center gap-2 text-base font-medium text-gray-950">
                <SparkleLine className="size-6" /> AI Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0 pt-5">
              <div className="border-b pb-5">
                <span className="text-base font-normal text-gray-600">
                  Results-driven product manager with 8+ years of experience leading cross-functional teams to deliver innovative solutions. Expert in
                  user research, data-driven decision making, and agile methodologies. Successfully launched 15+ products that generated $50M+ in
                  revenue.
                </span>
              </div>
              <div className="pt-5">
                <div className="space-y-3 py-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Phone className="size-[18px] text-gray-600" />
                      <span className="text-sm font-normal text-[#5C5C5C]">Primary Phone</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-950">
                        {revealedFields.has("phone") ? "+917550264569" : maskPhone("+917550264569")}
                      </span>
                      <button onClick={() => toggleReveal("phone")} className="text-blue-500 transition-colors hover:text-blue-600">
                        <Eye className="size-[18px]" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Phone className="size-[18px] text-gray-600" />
                      <span className="text-sm font-normal text-[#5C5C5C]">Secondary Phone</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-950">
                        {revealedFields.has("secondaryPhone") ? "+917550265678" : maskPhone("+917550265678")}
                      </span>
                      <button onClick={() => toggleReveal("secondaryPhone")} className="text-blue-500 transition-colors hover:text-blue-600">
                        <Eye className="size-[18px]" />
                      </button>
                    </div>
                  </div>

                  {/* email */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="size-[18px] text-gray-600" />
                      <span className="text-sm font-normal text-[#5C5C5C]">Official Email</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-950">
                        {revealedFields.has("officialEmail") ? "jamessmith@netflix.com" : maskEmail("jamessmith@netflix.com")}
                      </span>
                      <button onClick={() => toggleReveal("officialEmail")} className="text-blue-500 transition-colors hover:text-blue-600">
                        <Eye className="size-[18px]" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="size-[18px] text-gray-600" />
                      <span className="text-sm font-normal text-[#5C5C5C]">Personal Email</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-950">
                        {revealedFields.has("personalEmail") ? "jamessmith@gmail.com" : maskEmail("jamessmith@gmail.com")}
                      </span>
                      <button onClick={() => toggleReveal("personalEmail")} className="text-blue-500 transition-colors hover:text-blue-600">
                        <Eye className="size-[18px]" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Education */}
          <Card className="mt-4 max-w-2xl p-4">
            <CardHeader className="p-0">
              <CardTitle className="flex items-center gap-2 text-base font-medium text-gray-950">
                <GraduationCap className="size-6 text-gray-700" />
                Education
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0 pt-5">
              {/* Column Headers */}
              <div className="mb-4 grid grid-cols-3 items-center gap-4 rounded-lg  border-gray-200 bg-gray-50 px-3 py-2">
                <div className="text-sm font-normal text-gray-600">Degree</div>
                <div className="text-sm font-normal text-gray-600">School</div>
                <div className="text-right text-sm font-normal text-gray-600">Time Taken</div>
              </div>

              {/* Education Items */}
              <div className="space-y-3">
                {educationData.map((item, index) => (
                  <div key={index} className="grid grid-cols-3 gap-4 pb-5">
                    {/* Degree */}
                    <div className="text-sm font-medium text-gray-950">{item.degree}</div>

                    {/* School */}
                    <div>
                      <div className="text-sm font-medium text-gray-950">{item.school}</div>
                      <div className="text-sm text-gray-500">{item.location}</div>
                    </div>

                    {/* Time */}
                    <div className="text-right text-sm font-medium text-gray-950">{item.time}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* skills */}
          <Card className="mt-4 max-w-2xl p-4">
            <CardHeader className="p-0">
              <CardTitle className="flex items-center gap-2 text-base font-medium text-gray-950">
                <ToolsIcons /> Skills
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0 pt-5">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                  Product Strategy
                </Badge>
                <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                  Digital Transformation
                </Badge>
                <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                  A/B Testing
                </Badge>
                <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                  Content Strategy
                </Badge>
                <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                  User Experience
                </Badge>
                <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                  Data Analytics
                </Badge>
                <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                  Growth Marketing
                </Badge>
                <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                  Platform Development
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Certifications */}
          <Card className="mt-4 max-w-2xl p-4">
            <CardHeader className="p-0">
              <CardTitle className="flex items-center gap-2 text-base font-medium text-gray-950">
                <CertificateIcon className="size-5 text-gray-600" />
                Certifications
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0 pt-5">
              {/* Column Headers */}
              <div className="mb-4 grid grid-cols-3 items-center gap-4 rounded-lg border-gray-200 bg-gray-50 px-3 py-2">
                <div className="text-sm font-normal text-gray-600">Name</div>
                <div className="text-sm font-normal text-gray-600">Category</div>
                <div className="text-right text-sm font-normal text-gray-600">Date</div>
              </div>

              {/* Certification Items */}
              <div className="space-y-6">
                {certificationsData.map((item, index) => (
                  <div key={index} className="grid grid-cols-3 gap-4">
                    {/* Name */}
                    <div className="text-sm font-medium text-gray-950">{item.name}</div>

                    {/* Category */}
                    <div className="text-sm font-medium text-gray-950">{item.category}</div>

                    {/* Date */}
                    <div className="text-right text-sm font-medium text-gray-950">{item.date}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* right side */}
        <div className="flex flex-col items-start justify-center">
          <Card className="max-w-2xl p-4">
            <CardContent className=" p-0">
              <div className="flex items-center gap-3">
                <Button onClick={revealAllInfo} className="bg-[#335CFF] p-[10px] text-sm font-medium text-white hover:bg-[#335CFF] hover:text-white">
                  <Eye className="size-5" /> Reveal all info
                </Button>

                <Button variant="outline" className="p-[10px]">
                  <Globe className="size-5" />
                </Button>

                <Button variant="outline" className="p-[10px]">
                  <LinkedinIcon className="size-5" />
                </Button>

                <Button variant="outline" className="p-[10px]">
                  <FbIcon className="size-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* current company */}
          <Card className="mt-4 w-full p-4">
            <CardHeader className="p-0">
              <CardTitle className="flex items-center gap-2 text-base font-medium text-gray-950">
                <ArmchairIcon className="size-6" />
                Current Company
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0 pt-5">
              <div className="flex items-center justify-center gap-1 rounded-xl border border-border p-4">
                <AdobeIcon className="size-10" />
                <span className="text-2xl font-medium text-gray-950">Adobe</span>
              </div>

              <div className="my-4 space-y-3">
                {/* Business - SaaS */}
                <div className="flex justify-between">
                  <span className="text-sm font-normal text-[#5C5C5C]">Position</span>
                  <span className="text-sm font-medium text-gray-950">Senior Product Manager</span>
                </div>

                {/* Employees */}
                <div className="flex justify-between">
                  <span className="text-sm font-normal text-[#5C5C5C]">Seniority</span>
                  <span className="text-sm font-medium text-gray-950">Manager</span>
                </div>

                {/* Industry */}
                <div className="flex justify-between">
                  <span className="text-sm font-normal text-[#5C5C5C]">Job location</span>
                  <span className="text-sm font-medium text-gray-950">Malyasia</span>
                </div>

                {/* Revenue */}
                <div className="flex justify-between">
                  <span className="text-sm font-normal text-[#5C5C5C]">Time in current role</span>
                  <span className="text-sm font-medium text-gray-950">2 Years 3 Months</span>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button variant="outline" className="flex w-full items-center gap-2 text-sm font-medium text-gray-600">
                  <Globe className="size-5" />
                  Visit website
                </Button>
                <Button variant="outline" className="p-[10px]">
                  <LinkedinIcon className="size-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Experience */}
          <Card className="mt-4 w-full gap-4 p-4">
            <CardHeader className="p-0">
              <CardTitle className="flex items-center gap-2 text-base font-medium text-gray-950">
                <BriefcaseIcon className="size-5" /> Experience
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0 pt-5">
              <div className="flex flex-col items-start justify-center rounded-xl border px-3.5 py-5">
                <span className="text-sm font-normal text-gray-600">Total Experience Overall</span>
                <span className="text-[32px] font-medium text-gray-950">20 Years 11 Months</span>
              </div>

              <div className="my-4 border-b"></div>

              <div className="flex flex-col ">
                <span className="text-xs font-medium text-gray-600">Experience</span>

                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <Avatar className="flex size-8 items-center justify-center rounded-full border">
                      <AdobeIcon className="size-5" />
                    </Avatar>
                    <div className="flex flex-col items-start justify-center ">
                      <span className="text-sm font-medium text-gray-950">Senior Product Manager</span>
                      <span className="text-xs font-normal text-gray-600">Adobe • Chicago, IL</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-950">Current</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default CompanyDetails
